import React from 'react';
import PropTypes from "prop-types";
import {observable} from "mobx";
import { observer } from "mobx-react";
import { withStyles } from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";
import CircularProgress from "@material-ui/core/CircularProgress";
import DoneSharpIcon from "@material-ui/icons/DoneSharp";
import WarningIcon from "@material-ui/icons/Warning";
import ErrorShartIcon from "@material-ui/icons/ErrorSharp";
import Accordion from "@material-ui/core/Accordion";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import Skeleton from "@material-ui/lab/Skeleton";

import currentUser from "../../store/currentUser";
import LoadingButton from "../../components/utils/LoadingButton";
import ScenarioInputsComparison from "./ScenarioInputsComparison";
import Header from "../../components/utils/Header";
import { statGreen, statOrange, statRed } from "../../globals";
import { LOGS } from '../../labels';
import { findOccurences } from '../../utils';
import Scenario from "../../api/Scenario";


const styles = theme => ({
  success: {
    color: statGreen,
  },
  warning: {
    color: statOrange,
  },
  fail: {
    color: statRed,
  },
  summaryContent: {
    overflow: 'hidden',
  },
  unExpandable: {
    '&, &:hover:not(.Mui-disabled)': {
      cursor: 'default',
    },
  },
  unExpandableIcon: {
    visibility: 'hidden',
  },
  circularProgress: {
    fontSize: theme.typography.h5.fontSize,
    color: 'inherit',
  },
  log: {
    width: '100%',
    padding: theme.spacing(2),
    background: theme.palette.grey[800],
    color: theme.palette.common.white,
    borderRadius: theme.shape.borderRadius,
    overflow: 'auto',
    lineHeight: 'normal',
    maxHeight: '65vh',
  },
  shortLog: {
    width: '100%',
    overflow: 'auto',
  },
});


@withStyles(styles)
@observer
class ScenarioRunReport extends React.Component {
  static propTypes = {
    instanceId: PropTypes.number.isRequired,
    onScenarioReady: PropTypes.func,
  };
  static defaultProps = {
    onScenarioReady: null,
  };

  refreshDelay = 5000;
  @observable restartLoading = false;
  @observable now = new Date();

  constructor(props) {
    super(props);
    this.instance = Scenario.getInstance(props.instanceId);
    this.instance.refreshFromDb().then(() => {
      this.autoRefresh()
    });
  }

  componentDidMount() {
    this.nowInterval = setInterval(() => {
      this.now = new Date();
    }, 1000);
  }

  componentWillUnmount() {
    clearTimeout(this.refreshTimeout);
    clearInterval(this.nowInterval);
  }

  autoRefresh = () => {
    if (this.instance.isSuccess) {
      const { onScenarioReady } = this.props;
      if (onScenarioReady !== null) {
        onScenarioReady();
      }
    } else if (!this.instance.isFail) {
      this.refreshTimeout = setTimeout(async () => {
        if (this.instance.isSuccess || this.instance.isFail) {
          this.autoRefresh();
        } else {
          try {
            await this.instance.refreshFromDb();
          } finally {
            this.autoRefresh();
          }
        }
      }, this.refreshDelay);
    }
  };

  restartRun = async () => {
    this.restartLoading = true;
    clearTimeout(this.refreshTimeout);
    await this.instance.runModel();
    await this.instance.refreshFromDb();
    this.autoRefresh();
    this.restartLoading = false;
  };

  checkSolution(step){
    if(findOccurences('Optimal solution found', step.log) < 2) {
      step.status = Scenario.WARNING;
      for (const [warning, message] of Object.entries(LOGS.OPTIMIZATION_WARNINGS)) 
        if(findOccurences(warning,step.log) > 0) 
          step.error = message;
    }
    return step;
  }

  get logs() {
    const { classes } = this.props;
    const { steps } = this.instance;
    return steps.map((step, index) => {
      if(step.name === "optimization" && step.status === Scenario.SUCCESS) step = this.checkSolution(step)
      const endedAt = step.ended_at;
      const startedAt = step.started_at;
      const isRunning = step.status === Scenario.STARTED;
      let duration = null;
      if (startedAt !== null) {
        duration = new Date(
          (endedAt === null ? this.now : new Date(endedAt))
          - new Date(startedAt)
        ).toISOString().substr(11, 8);
      }
      let className = null;
      let icon = (
        <CircularProgress
          className={classes.circularProgress}
          size="1em"
          variant={isRunning ? 'indeterminate' : 'static'}
        />
      );
      if (step.status === Scenario.SUCCESS) {
        className = classes.success;
        icon = <DoneSharpIcon color="inherit" />;
      } else if (step.status === Scenario.WARNING) {
        className = classes.warning;
        icon = <WarningIcon color="inherit" />;
      } else if (step.status === Scenario.FAIL) {
        className = classes.fail;
        icon = <ErrorShartIcon color="inherit" />;
      }
      const canViewLog = currentUser.can('athena.view_logs');
      const expandable = canViewLog || (step.error !== '');
      if (!expandable) {
        className = `${className} ${classes.unExpandable}`;
      }
      const disabled = [Scenario.CREATED, Scenario.QUEUED].includes(step.status);
      return (
        <Accordion key={`${step.name}-${expandable}`} disabled={disabled} {...(expandable ? {} : { expanded: false })}>
          <AccordionSummary
            className={className}
            classes={{ content: classes.summaryContent }}
            expandIcon={<ExpandMoreIcon />}
            IconButtonProps={{
              disableRipple: true,
              className: expandable ? null : `${classes.unExpandable} ${classes.unExpandableIcon}`,
            }}
          >
            <Grid container spacing={1} justify="space-between" alignItems="center" wrap="nowrap">
              <Grid item>
                <Grid container spacing={1} alignItems="center" wrap="nowrap">
                  <Grid item>
                    <Box display="block" clone>
                      {icon}
                    </Box>
                  </Grid>
                  <Grid item>
                    {step.name.toUpperCase().replaceAll('_', ' ')}
                  </Grid>
                </Grid>
              </Grid>
              <Grid item zeroMinWidth>
                <Typography noWrap>
                  {
                    (disabled && index === 0)
                    ? 'Scenario queued, waiting for available server resources to start'
                    : `${step.error.split('\n')[0]}${step.error.includes('\n') ? '…' : ''}`
                  }
                </Typography>
              </Grid>
              <Grid item>
                {duration}
              </Grid>
            </Grid>
          </AccordionSummary>
          {expandable && (
            <AccordionDetails>
              <Grid container direction="column" spacing={2} wrap="nowrap">
                {canViewLog && (
                  <Grid item>
                    <pre className={classes.log}>{step.log}</pre>
                  </Grid>
                )}
                {step.error !== '' && (
                  <Grid item>
                    <pre className={`${classes.shortLog} ${className} `}>{step.error}</pre>
                  </Grid>
                )}
              </Grid>
            </AccordionDetails>
          )}
        </Accordion>
      );
    });
  }

  get content() {
    if (!this.instance.isLoaded) {
      return <Skeleton variant="rect" height={550} />;
    }
    return (
      <Grid
        container
        direction="column"
        spacing={4}
        justify="center"
        wrap="nowrap"
      >
        <Grid item>
          {this.logs}
        </Grid>
        {
          this.instance.isFail
          ? (
            <Grid item>
              <LoadingButton
                variant="outlined"
                onClick={this.restartRun}
                loading={this.restartLoading}
                fullWidth
              >
                Restart run
              </LoadingButton>
            </Grid>
          )
          : null
        }
        <Grid item>
          <ScenarioInputsComparison scenario={this.instance} />
        </Grid>
      </Grid>
    );
  }

  render() {
    return (
      <Grid container direction="column" spacing={4} wrap="nowrap">
        <Grid
          item
          container
          spacing={1}
          justify="space-between"
          alignItems="center"
          wrap="nowrap"
        >
          <Grid item>
            <Header title={this.instance.toString()} />
          </Grid>
        </Grid>
        <Grid item container>
          {this.content}
        </Grid>
      </Grid>
    );
  }
}


export default ScenarioRunReport;
